import React, { useState } from "react";
import { TreeSelect, Button, Input } from "antd";
import { IProcessesData, ISaleChannelData } from "store/lobby/lobby-model";
import { searchIdByDescriptionSelected, searchProcessesIdByDescriptionSelected } from "utils/functions";
import useTranslation from "utils/hooks/useTranslation";
import * as S from "./styled/index.styled";

export interface IFilterOptions {
  allProcessesList: Array<IProcessesData>;
  salesChannelList: Array<ISaleChannelData>;
  processValue: Array<string>;
  salesChannelValue: Array<string>;
  setProcessValue: React.Dispatch<React.SetStateAction<Array<string>>>;
  setSalesChannelValue: React.Dispatch<React.SetStateAction<Array<string>>>;
  vehicleDashboardChangeFilterOptionsValue: (...args) => void;
  vehicleDashboardFetchRegisterAsync: (...args) => void;
  vehicleDashboardResetVehiclesData: () => void;
}

const FilterOptions = ({
  processValue,
  allProcessesList,
  salesChannelList,
  salesChannelValue,
  setProcessValue,
  setSalesChannelValue,
  vehicleDashboardResetVehiclesData,
  vehicleDashboardFetchRegisterAsync,
  vehicleDashboardChangeFilterOptionsValue,
}: IFilterOptions): JSX.Element => {
  const { SHOW_PARENT } = TreeSelect;
  const SELECT_PROCESSES = useTranslation({ id: "select_processes" });
  const SELECT_SALES_CHANNEL = useTranslation({ id: "select_channels" });
  const PROCESSES = useTranslation({ id: "processes" });
  const CHANNEL = useTranslation({ id: "channel" });
  const SEARCH = useTranslation({ id: "search" });
  const SEARCH_PLACEHOLDER = useTranslation({ id: "search_for_a_license_plate_or_document" });
  const [searchValue, setSearchValue] = useState("");

  const checkIfTheFiltersAreFilled = processValue.length > 0 || salesChannelValue.length > 0 || searchValue !== "";

  const listProcessesData = (data) => {
    const processes = data.map((x) => {
      return {
        value: x.description,
        title: x.description,
        children: [],
      };
    });
    return processes;
  };

  const listSaleChannelData = (data) => {
    const saleChannel = data.map((x) => {
      return {
        value: x.description,
        title: x.description,
        children: [],
      };
    });
    return saleChannel;
  };

  const processProps = {
    treeData: listProcessesData(allProcessesList),
    placeholder: SELECT_PROCESSES,
    value: processValue,
    onChange: (newValue: string[]) => setProcessValue(newValue),
    treeCheckable: true,
    showCheckedStrategy: SHOW_PARENT,
    style: {
      width: "100%",
    },
  };

  const salesChannelProps = {
    treeData: listSaleChannelData(salesChannelList),
    placeholder: SELECT_SALES_CHANNEL,
    value: salesChannelValue,
    onChange: (newValue: string[]) => setSalesChannelValue(newValue),
    treeCheckable: true,
    showCheckedStrategy: SHOW_PARENT,
    style: {
      width: "100%",
    },
  };

  const onFilterOptionsQuery = () => {
    const processId = searchProcessesIdByDescriptionSelected(processValue, allProcessesList);
    const saleChannelId = searchIdByDescriptionSelected(salesChannelValue, salesChannelList);
    vehicleDashboardResetVehiclesData();
    vehicleDashboardChangeFilterOptionsValue("processId", processId);
    vehicleDashboardChangeFilterOptionsValue("saleChannelId", saleChannelId);
    vehicleDashboardChangeFilterOptionsValue("searchFilter", searchValue);
    vehicleDashboardFetchRegisterAsync();
  };

  const onCleanFilters = () => {
    vehicleDashboardResetVehiclesData();
    setProcessValue([]);
    setSalesChannelValue([]);
    setSearchValue("");
    vehicleDashboardChangeFilterOptionsValue("processId", []);
    vehicleDashboardChangeFilterOptionsValue("saleChannelId", []);
    vehicleDashboardChangeFilterOptionsValue("searchFilter", "");
    vehicleDashboardFetchRegisterAsync();
  };

  return (
    <S.FilterOptionsContainer>
      <div>
        <S.Label>{SEARCH}</S.Label>
        <S.FormItem>
          <Input style={S.DescriptionField} placeholder={SEARCH_PLACEHOLDER} maxLength={20} value={searchValue} onChange={(e) => setSearchValue(e.target.value)} />
        </S.FormItem>
      </div>
      <div>
        <S.Label>{CHANNEL}</S.Label>
        <TreeSelect key="saleChannel" style={S.SelectField} {...salesChannelProps} maxTagCount="responsive" allowClear />
      </div>
      <div>
        <S.Label>{PROCESSES}</S.Label>
        <TreeSelect key="process" style={S.SelectField} {...processProps} maxTagCount="responsive" allowClear />
      </div>
      <S.ButtonsFilter>
        <Button size="large" onClick={onFilterOptionsQuery} className="ant-btn-primary" data-testid="confirmFilter">
          {useTranslation({ id: "filter" })}
        </Button>
        <Button size="large" className="secondaryButton" onClick={onCleanFilters} disabled={!checkIfTheFiltersAreFilled} data-testid="cleanFilters">
          {useTranslation({ id: "clean" })}
        </Button>
      </S.ButtonsFilter>
    </S.FilterOptionsContainer>
  );
};

export default FilterOptions;
